import { Component, OnInit, ViewChild } from '@angular/core';
import { DashboardService, DashboardFilters } from '../services/dashboard.service';
import { SucursalesService } from '../services/sucursales.service';
import { DatesHelpersService } from '../services/tools/dates-helpers.service';
import { HelpersService } from '../services/tools/helpers.service';

// Importaciones para las Gráficas (Chart.js / ng2-charts)
import { ChartDataSets, ChartOptions, ChartType } from 'chart.js';
import { BaseChartDirective, Color } from 'ng2-charts';

// Importaciones para Exportación de Reportes (PDF)
import * as jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {
    @ViewChild(BaseChartDirective) chart: BaseChartDirective;

    public sucursalPorDefecto: string = "";
    public isLoading: boolean = false;
    public muestraBarras: boolean = false;

    public fechaInicio: string = null;
    public fechaFin: string = null;
    public sucursales: any[] = [];
    public sucursal: any = { id: null, nombre: 'TODAS' };

    public kpis = { totalColocado: 0, totalGastos: 0, clientesActivos: 0, carteraVencida: 0 };

    // --- CONFIGURACIÓN DE GRÁFICA ---
    public barChartType: ChartType = 'bar';
    public barChartLegend: boolean = false;
    public barChartLabels: string[] = [];
    public barChartData: ChartDataSets[] = [{ data: [], label: 'Colocación Mensual' }];

    public barChartOptions: ChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            yAxes: [{ ticks: { beginAtZero: true, callback: (v: number) => '$' + v.toLocaleString('es-MX') } }],
            xAxes: [{ ticks: { autoSkip: false, maxRotation: 45, minRotation: 45 } }]
        },
        tooltips: { mode: 'index', intersect: false }
    };

    public barChartColors: Color[] = [{ backgroundColor: '#0acf97', borderColor: '#1e7e34', borderWidth: 1 }];

    constructor(
        private dashboardService: DashboardService,
        private sucursalesService: SucursalesService,
        private datesHelper: DatesHelpersService,
        private helperService: HelpersService
    ) { }

    ngOnInit() {
        this.inicializarFiltros();
         this.getSucursales();
        this.cargarDashboard();
    }

    private inicializarFiltros() {
        const defaultDates = this.datesHelper.getMonthDate();
        const currentYear = defaultDates.fechaFin.split("-")[0];

        this.fechaInicio = `${currentYear}-01-01`;
        this.fechaFin = defaultDates.fechaFin;
    }

    private cargarSucursales() {
        // NOTA: Si modificas SucursalesService en el futuro para que use .map(), 
        // tendrías que retirar el .json() de aquí también. Por ahora se queda igual.
        this.sucursalesService.getSelect().subscribe(
            (response) => {
                const jsonResult = response.json();
                if (jsonResult.content) {
                    this.sucursales = [{ id: null, nombre: 'TODAS LAS SUCURSALES' }, ...jsonResult.content];

                    if (jsonResult.content.length === 1) {
                        this.sucursal = jsonResult.content[0];
                    }
                }
            },
            (error) => this.helperService.httpGlobalError(error)
        );
    }

    /**
     * 3. Orquestador de carga de datos
     */
    public cargarDashboard() {
        this.isLoading = true;

        const filters: DashboardFilters = {
            'start-date': this.fechaInicio,
            'end-date': this.fechaFin,
            ...(this.sucursal.id && { idSucursales: this.sucursal.id })
        };

        this.dashboardService.getResumenGeneral(filters).subscribe(
            (res: any) => {
                // CORRECCIÓN INTERMEDIA: Validamos si la respuesta viene directa o envuelta en ".content"
                // Si 'res.metrics' existe, la respuesta viene directa; si no, tomamos 'res.content'
                const data = (res && res.metrics) ? res : (res && res.content ? res.content : null);

                if (data) {
                    // --- ASIGNACIÓN REACTIVA DE KPIs ---
                    this.kpis.totalColocado = (data.metrics && data.metrics.total_colocado) ? data.metrics.total_colocado : 0;
                    this.kpis.totalGastos = (data.metrics && data.metrics.total_gastos) ? data.metrics.total_gastos : 0;
                    this.kpis.clientesActivos = (data.metrics && data.metrics.clientes_activos) ? data.metrics.clientes_activos : 0;
                    this.kpis.carteraVencida = (data.metrics && data.metrics.cartera_vencida) ? data.metrics.cartera_vencida : 0;

                    // --- PROCESAMIENTO DE LA GRÁFICA ---
                    if (data.tendencia_colocacion && data.tendencia_colocacion.length > 0) {
                        const nuevosLabels: string[] = [];
                        const nuevosValores: number[] = [];

                        data.tendencia_colocacion.forEach((item: any) => {
                            nuevosLabels.push(item.mes);
                            nuevosValores.push(Number(item.total));
                        });

                        // Asignamos los nuevos arrays para romper la referencia anterior
                        this.barChartLabels = nuevosLabels;
                        this.barChartData = [{ data: nuevosValores, label: 'Colocación Mensual' }];
                        this.muestraBarras = true;

                        // CORRECCIÓN CRÍTICA: Forzado de ciclo de renderizado en Chart.js (ng2-charts)
                        setTimeout(() => {
                            if (this.chart && this.chart.chart) {
                                this.chart.chart.update();
                            }
                        }, 50);

                    } else {
                        this.muestraBarras = false;
                    }
                } else {
                    console.warn('⚠️ La respuesta de la API no contiene una estructura reconocible:', res);
                    this.muestraBarras = false;
                }

                this.isLoading = false;
            },
            (error) => {
                this.isLoading = false;
                this.helperService.httpGlobalError(error);
            }
        );
    }
    private getSucursales() {
        this.isLoading = true;
        this.sucursalesService.getSelect().subscribe(
            (response) => {
                const jsonResult = response.json();
                if (jsonResult.content) {
                    this.sucursales = jsonResult.content;
                    console.log("Sucursales: ", this.sucursales);
                    if (this.sucursales.length > 1) {
                        this.sucursalPorDefecto = "Seleccione";
                    } else if (this.sucursales.length > 0) {
                        this.sucursal = this.sucursales[0];
                    }
                    //this.getGastos();
                    this.isLoading = false;
                }

            }, error => {
            // se resuelve error 403 y 500
            this.isLoading = false;
            this.helperService.httpGlobalError(error);

            if (error.status == 400) {
                this.helperService.notification('ERROR', 'No se completó la acción. Por favor, intenta más tarde.', 'error');
            }
        }
        );
    }
    public sucursalChanged(sucursalSeleccionada: any) {
        if (sucursalSeleccionada) {
            this.sucursal = sucursalSeleccionada;
            this.cargarDashboard();
        }
    }

    public chartClicked(event: any): void {
        if (event.active && event.active.length > 0) {
            const chartElement = event.active[0];
            const index = chartElement._index;
            const mesSeleccionado = this.barChartLabels[index];
            const valorSeleccionado = this.barChartData[0].data[index];

            console.log(`Análisis drill-down para el mes de: ${mesSeleccionado}, total: ${valorSeleccionado}`);
        }
    }

    public downloadPDF() {
        this.isLoading = true;

        const DATA = document.getElementById('dashboardPrintArea');
        if (!DATA) {
            console.error('No se encontró el contenedor #dashboardPrintArea en el HTML');
            this.isLoading = false;
            return;
        }

        const options = {
            background: '#f4f5f7',
            scale: 2,
            useCORS: true
        };

        html2canvas(DATA, options).then((canvas) => {
            const doc = new jsPDF('l', 'mm', 'a4');
            const imgWidth = doc.internal.pageSize.getWidth() - 20;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            const imgData = canvas.toDataURL('image/png');

            doc.addImage(imgData, 'PNG', 10, 10, imgWidth, imgHeight, undefined, 'FAST');
            doc.save(`Reporte_Gerencial_Klabe_${this.fechaFin}.pdf`);

            this.isLoading = false;
        }).catch((err) => {
            console.error('Error generando captura canvas:', err);
            this.isLoading = false;
        });
    }
}


// WEBPACK FOOTER //
// ./src/app/dashboard/dashboard.component.ts